<template>
    <div class="container mx-auto px-4 py-8">
        <!-- Class Filter Pills -->
        <div class="flex items-center gap-4 flex-wrap mb-10">
            <div v-for="classItem in classList"
                 :key="classItem.id"
                 :class="{'bg-primary border-primary text-white': classItem.id === selectedClassId, 'border-slate-300 bg-white hover:outline-1 outline-primary': classItem.id !== selectedClassId}"
                 class="border-1 px-4 py-2 rounded-xl cursor-pointer transition-all duration-100"
                 @click="selectedClassId = classItem.id"
            >
                {{ classItem.short_name }} - {{ classItem.class_name_alias || classItem.logic_name }}
            </div>
        </div>

        <!-- Class Content -->
        <div v-for="classItem in classList" :key="'content-' + classItem.id">
            <div v-show="classItem.id === selectedClassId" class="space-y-10">
                
                <!-- Class Article (if available) -->
                <div v-if="classItem.page && classItem.page.page_content" class="prose prose-xl max-w-4xl mx-auto">
                    <div v-if="classItem.page.main_image" class="mb-6">
                        <img :src="getImageUrl(classItem.page.main_image)" 
                             :alt="classItem.page.img_alt || classItem.page.page_title"
                             class="w-full rounded-xl shadow-lg"
                             loading="lazy" />
                    </div>
                    <h2 v-if="classItem.page.page_title" class="text-3xl font-bold mb-4">
                        {{ classItem.page.page_title }}
                    </h2>
                    <div v-html="stripStylesAndScripts(classItem.page.page_content)"></div>
                </div>

                <!-- Course Booking Section -->
                <div v-if="showCourses" class="max-w-6xl mx-auto">
                    <h2 class="text-3xl font-bold mb-6">{{ $t('Kurspåmelding') }}</h2>
                    <Courselist></Courselist>
                </div>

                <!-- Price Table -->
                <div class="max-w-6xl mx-auto">
                    <h2 class="text-3xl font-bold mb-6">
                        {{ $t('Priser {className}', { className: classItem.class_name_alias || classItem.short_name }) }}
                    </h2>
                    
                    <div v-for="type in classItem.types" :key="type.name" class="mb-8">
                        <h3 class="text-2xl font-semibold mb-4">{{ type.name }}</h3>
                        
                        <table class="table-auto text-left w-full">
                            <thead>
                                <tr class="text-lg">
                                    <th class="font-normal py-3">{{ $t('Produkt') }}</th>
                                    <th class="text-right font-normal py-3">{{ $t('Pris') }}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="product in type.products" 
                                    :key="product.product_id"
                                    class="border-b-1 border-slate-300 hover:border-primary"
                                >
                                    <td class="py-3">
                                        <div>
                                            <span class="font-medium">{{ product.product_name }}</span>
                                            <span v-if="product.duration" class="text-sm text-slate-500">
                                                ({{ product.duration }} min)
                                            </span>
                                        </div>
                                        
                                        <!-- Package Content -->
                                        <ul v-if="product.package_content && product.package_content.length" 
                                            class="mt-1 text-sm text-slate-600 space-y-0.5">
                                            <li v-for="content in product.package_content" :key="content.product_id" class="italic">
                                                {{ content.quantity }} x {{ content.product_name }}
                                            </li>
                                        </ul>
                                        
                                        <!-- Product Description -->
                                        <div v-if="product.product_description_on_web" 
                                             class="mt-1 text-sm text-slate-600"
                                             v-html="product.product_description_on_web"
                                        ></div>
                                    </td>
                                    
                                    <td class="py-3 text-right font-semibold">
                                        {{ moneyFormat(product.total_amount_include_fee) }}
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>

                    <!-- Price Terms -->
                    <div v-if="pricelistTermsText" class="mt-8 bg-slate-50 border border-slate-200 rounded-xl p-6">
                        <h4 class="text-xl font-semibold mb-3">{{ $t('Informasjon om priser') }}</h4>
                        <div class="text-sm text-slate-600" v-html="pricelistTermsText.replace(/\n/g, '<br>')"></div>
                    </div>
                </div>

                <!-- Teachers Section -->
                <div v-if="getTeachersForClass(classItem).length > 0" class="max-w-6xl mx-auto">
                    <h2 class="text-3xl font-bold mb-6">
                        {{ $t('Lærere {className}', { className: classItem.class_name_alias || classItem.short_name }) }}
                    </h2>
                    
                    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <div v-for="employee in getTeachersForClass(classItem)" 
                             :key="employee.user_id"
                             class="bg-white border-1 border-slate-200 rounded-xl overflow-hidden hover:shadow-xl hover:scale-105 transition-all duration-300"
                        >
                            <!-- Employee Image -->
                            <div class="relative aspect-[1/1] bg-slate-100 overflow-hidden">
                                <img
                                    :src="employee.user_image_src"
                                    :alt="employee.call_name"
                                    class="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                    loading="lazy"
                                />

                                <!-- Gradient Overlay -->
                                <div class="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent"></div>

                                <!-- Employee Info Overlay -->
                                <div class="absolute inset-0 flex items-end p-4 text-white">
                                    <div class="flex flex-col gap-1">
                                        <h3 class="text-xl font-semibold drop-shadow-lg mb-1">
                                            {{ employee.call_name }}
                                        </h3>
                                        
                                        <!-- Classes -->
                                        <div v-if="employee.classlist" class="flex items-start gap-2">
                                            <span class="text-sm text-ellipsis">{{ employee.classlist }}</span>
                                        </div>

                                        <!-- Phone -->
                                        <a v-if="employee.mobile"
                                           :href="'tel:' + employee.mobile"
                                           class="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                                        >
                                            <span class="hover:underline">{{ employee.mobile }}</span>
                                        </a>

                                        <!-- Email -->
                                        <a v-if="employee.email"
                                           :href="'mailto:' + employee.email"
                                           class="flex items-center gap-2 text-sm hover:text-primary transition-colors"
                                        >
                                            <span class="hover:underline truncate">{{ employee.email }}</span>
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';

const props = defineProps({
    title: {
        type: String,
        required: false,
        default: null
    }
});

// These will be populated from the window object set by Twig
const classList = ref([]);
const employees = ref([]);
const pricelistTermsText = ref('');
const showCourses = ref(true);
const officeWebName = ref('');

// Selected class ID (defaults to first class)
const selectedClassId = ref(null);

onMounted(() => {
    // Load data from window object (set by Twig template)
    if (window.classListData) {
        classList.value = window.classListData.pricelist || [];
        employees.value = window.classListData.employees || [];
        pricelistTermsText.value = window.classListData.pricelistTermsText || '';
        showCourses.value = window.classListData.showCourses !== false;
        officeWebName.value = window.classListData.officeWebName || '';
        
        // Set initial selected class to first class
        if (classList.value.length > 0) {
            selectedClassId.value = classList.value[0].id;
        }

        // Check for URL parameter to pre-select a class
        const urlParams = new URLSearchParams(window.location.search);
        const classIdParam = urlParams.get('classId') || urlParams.get('class_id') || urlParams.get('class');
        if (classIdParam) {
            const classId = parseInt(classIdParam);
            if (classList.value.find(c => c.id === classId)) {
                selectedClassId.value = classId;
            }
        }
    }
});

const moneyFormat = (price) => {
    return price ? window.TABS.L10n.money.parse(price).format() : '-';
};

const getImageUrl = (imagePath) => {
    return `https://cdn.tabs.no/skole/${officeWebName.value}/img/nyheter/${imagePath}`;
};

const getTeachersForClass = (classItem) => {
    return employees.value.filter(employee => {
        if (!employee.classes || !Array.isArray(employee.classes)) {
            return false;
        }
        return employee.classes.some(c => c.short_name === classItem.short_name);
    });
};

const stripStylesAndScripts = (html) => {
    if (!html) return '';
    
    // Remove <script> tags and their content
    let cleaned = html.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
    
    // Remove <style> tags and their content
    cleaned = cleaned.replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '');
    
    // Replace style=" with data-style=" to disable inline styles
    cleaned = cleaned.replace(/style="/gi, 'data-style="');
    
    // Also handle single quotes
    cleaned = cleaned.replace(/style='/gi, "data-style='");
    
    return cleaned;
};

// Helper function to be called by the API course loader
if (typeof window !== 'undefined') {
    window.checkIfEmptyResult = (el, name) => {
        const loaders = document.querySelectorAll('.loader');
        loaders.forEach(loader => {
            loader.style.display = 'none';
        });

        const apiMain = el.querySelector('#api-main>div');
        if (apiMain && apiMain.children.length === 0) {
            const noCoursesMsg = el.parentElement.querySelector('.no-courses-message');
            if (noCoursesMsg) {
                noCoursesMsg.style.display = 'block';
            }
        }
    };
}
</script>

<style scoped>
.loader {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 2rem;
}

.spinner {
    width: 40px;
    height: 40px;
    border: 4px solid #f3f4f6;
    border-top-color: var(--tabs-primary-color, #000);
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    to { transform: rotate(360deg); }
}
</style>
